import { Component, Inject, Injector, Type, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CreateOutputsComponent } from 'src/app/components/home/output-assets/actions/create-outputs/create-outputs.component';
import {
  LabelDataProductCreateComponent,
  LabelDataReviewDialogData,
} from 'src/app/components/home/label-data/label-data-product-create/label-data-product-create.component';
import {
  CreateProductDataComponent,
  CreateProductDataDialogData,
} from 'src/app/components/shared/create-product-data/create-product-data.component';
import { CreateOutputsCollectionComponent } from 'src/app/components/home/output-collections/actions/create-outputs-collection.component';
import { DialogResult } from 'src/app/models/dialog-result';
import { IProduct } from 'src/app/models/products/product';
import { OutputConfirmProductSearch } from 'src/app/models/products/productSearch';

export interface CreateCollectionWizardData {
  readonly organizationId: string;
  readonly batchId: string;
  readonly productIds: string[];
  readonly products: IProduct[];
}

@Component({
  selector: 'app-create-collection-wizard',
  templateUrl: './create-collection-wizard.component.html',
  styleUrls: ['./create-collection-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class CreateCollectionWizardComponent {
  readonly steps = ['Create Outputs', 'Create Product Labels', 'Create Product Data File', 'Create Collection'];
  readonly stepComponents: Type<unknown>[] = [
    CreateOutputsComponent,
    LabelDataProductCreateComponent,
    CreateProductDataComponent,
    CreateOutputsCollectionComponent,
  ];

  currentStep = 1;
  completedSteps: number[] = [];

  private readonly stepInjectors: Injector[];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CreateCollectionWizardData,
    public dialogRef: MatDialogRef<CreateCollectionWizardComponent>,
    parentInjector: Injector,
  ) {
    const outputsData: OutputConfirmProductSearch = {
      organizationId: data.organizationId,
      batchId: data.batchId,
      isBatch: false,
      productIds: data.productIds,
    };
    const labelData: LabelDataReviewDialogData = {
      productIds: data.productIds,
      products: data.products,
    };
    const productDataData: CreateProductDataDialogData = {
      productIds: data.productIds,
      organizationId: data.organizationId,
      batchId: data.batchId,
    };
    const collectionData: OutputConfirmProductSearch = {
      organizationId: data.organizationId,
      batchId: data.batchId,
      isBatch: false,
      productIds: data.productIds,
    };

    this.stepInjectors = [
      Injector.create({
        parent: parentInjector,
        providers: [
          { provide: MAT_DIALOG_DATA, useValue: outputsData },
          { provide: MatDialogRef, useValue: this.makeStubDialogRef(1) },
        ],
      }),
      Injector.create({
        parent: parentInjector,
        providers: [
          { provide: MAT_DIALOG_DATA, useValue: labelData },
          { provide: MatDialogRef, useValue: this.makeStubDialogRef(2) },
        ],
      }),
      Injector.create({
        parent: parentInjector,
        providers: [
          { provide: MAT_DIALOG_DATA, useValue: productDataData },
          { provide: MatDialogRef, useValue: this.makeStubDialogRef(3) },
        ],
      }),
      Injector.create({
        parent: parentInjector,
        providers: [
          { provide: MAT_DIALOG_DATA, useValue: collectionData },
          { provide: MatDialogRef, useValue: this.makeStubDialogRef(4) },
        ],
      }),
    ];
  }

  get currentStepComponent(): Type<unknown> {
    return this.stepComponents[this.currentStep - 1];
  }

  get currentStepInjector(): Injector {
    return this.stepInjectors[this.currentStep - 1];
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.steps.length) {
      this.currentStep = step;
    }
  }

  private makeStubDialogRef(step: number): MatDialogRef<unknown> {
    const noop = { subscribe: () => ({ unsubscribe: () => undefined }) };
    const stub = {
      close: (result: DialogResult) => this.onEmbeddedClose(step, result),
      afterClosed: () => noop,
      afterOpened: () => noop,
      backdropClick: () => noop,
      keydownEvents: () => noop,
      addPanelClass: () => stub,
      removePanelClass: () => stub,
      updatePosition: () => stub,
      updateSize: () => stub,
      disableClose: false,
    };
    return stub as unknown as MatDialogRef<unknown>;
  }

  private onEmbeddedClose(fromStep: number, result: DialogResult): void {
    if (result === DialogResult.Cancel) {
      this.dialogRef.close();
      return;
    }
    if (!this.completedSteps.includes(fromStep)) {
      this.completedSteps = [...this.completedSteps, fromStep];
    }
    if (fromStep < this.steps.length) {
      this.currentStep = fromStep + 1;
    } else {
      this.dialogRef.close();
    }
  }
}
